import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { Button } from '@/components/ui/button';
import {
  TOKEN_STATE_LABEL,
  tokenLastUseSentence,
  tokenMintSentence,
  tokenRevokedSentence,
  tokenState,
} from '@/lib/token-labels';
import type { OperatorToken } from '@matter_extractor/shared';

/**
 * « Mes jetons » - mine only, newest first, revoked ones included (§20.2,
 * issue #84).
 *
 * The order is the server's and is not re-sorted here: `listOperatorTokens`
 * answers newest first, and a list that sorted a second time would be a second
 * spelling of « what order is this in ».
 *
 * ## A revoked token stays, GREYED
 *
 * The régime of a revoked link in Réglages > Partage, and the same reason said
 * by the backend itself: « a row that disappeared would leave nothing to read
 * when somebody asks what happened to a key ». The grey is on the ground and
 * the ink; what goes away is the button that could do nothing, because a
 * revoked token never reopens.
 *
 * ## Three facts per row, and « jamais » is one of them
 *
 * The label, the mint day, and the last use. The last use is the fact this
 * screen exists to make readable: a token minted, pasted nowhere and forgotten
 * reads « jamais », which is the row to revoke first - and a sentence in a
 * README could never have said which one.
 */

export interface TokenListProps {
  readonly tokens: readonly OperatorToken[];
  /** The id a revocation is in flight for, so only its own row goes inert. */
  readonly revokingId: string | null;
  readonly onRevoke: (token: OperatorToken) => void;
}

export function TokenList({ tokens, revokingId, onRevoke }: TokenListProps) {
  if (tokens.length === 0) {
    return (
      <p data-testid="jetons-liste-vide" className="text-gray-600 text-sm">
        Aucun jeton pour l’instant. Le formulaire ci-dessus en frappe un.
      </p>
    );
  }

  return (
    <ul data-testid="jetons-liste" className="space-y-3">
      {tokens.map((token) => {
        const state = tokenState(token);
        const revoked = state === 'revoked';
        return (
          <li
            key={token.id}
            data-testid="jetons-jeton"
            data-state={state}
            className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border-2 border-gray-200 p-4 ${
              revoked ? 'bg-gray-50' : 'bg-white'
            }`}
          >
            <h4
              data-testid="jetons-jeton-libelle"
              className={`font-bold text-base ${revoked ? 'text-gray-600' : 'text-bleu-nuit'}`}
            >
              {token.label}
            </h4>
            {/*
              THE pill of the administration (`entretiens/status-badge.tsx`),
              not a ninth spelling of it: an operator learns what a pill means
              from its shape, and a shape that changes per screen teaches
              nothing. Its `success` and `neutral` tones are exactly the two
              this list needs - vert for a key that opens, grey for one that
              does not - and `danger` would be wrong on a revoked token, which
              is a fact and not a warning.
            */}
            <StatusBadge tone={revoked ? 'neutral' : 'success'} data-testid="jetons-jeton-etat">
              {TOKEN_STATE_LABEL[state]}
            </StatusBadge>
            <span data-testid="jetons-jeton-frappe" className="text-gray-600 text-sm">
              {tokenMintSentence(token)}
            </span>
            <span data-testid="jetons-jeton-usage" className="text-gray-600 text-sm">
              {tokenLastUseSentence(token)}
            </span>
            {revoked ? (
              <span data-testid="jetons-jeton-revoque" className="text-gray-600 text-sm">
                {tokenRevokedSentence(token)}
              </span>
            ) : (
              <span className="ml-auto">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  data-testid="jetons-jeton-revoquer"
                  disabled={revokingId === token.id}
                  onClick={() => onRevoke(token)}
                >
                  Révoquer
                </Button>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
